


















































































































































































import { Component, Mixins } from "vue-property-decorator";
import AxiosMixin from "@/mixins/axiosMixin";
import UtilMixin from "@/mixins/utilMixin";
import LoginMixin from "#/mixins/loginMixin";
import * as constant from "#/const";

/** 許可端末（表示用） */
interface DeviceView {
  id: number;
  ewell_user_id: number;
  staff_name: string;
  device_name: string;
  user_agent: string;
  status: number;
  last_access_at: string;
  created_at: string;
}

/** 発行対象の職員選択肢 */
interface StaffChoice {
  ewell_user_id: number;
  name: string;
  office_name: string;
}

const STATUS_PENDING = 0;
const STATUS_ACTIVE = 1;

@Component
export default class DeviceManagement extends Mixins(
  AxiosMixin,
  UtilMixin,
  LoginMixin
) {
  private devices: DeviceView[] = [];
  private staffs: StaffChoice[] = [];
  private issueEwellUserId = 0;
  private deviceRestrictionMode = 0;
  private maxAllowedDevices = 3;

  private codeDialog = false;
  private issuedCode = "";
  private issuedExpireMinutes = 0;

  /** 所長以上か */
  private get isChiefOrAbove(): boolean {
    const auth = constant.OFFICE_STAFF.AUTH;
    return (
      this.loginUserAuthId === auth.CHIEF ||
      this.loginUserAuthId === auth.HOME ||
      this.loginUserAuthId === auth.ADMIN
    );
  }

  private get pendingDevices(): DeviceView[] {
    return this.devices.filter(d => d.status === STATUS_PENDING);
  }

  private get activeDevices(): DeviceView[] {
    return this.devices.filter(d => d.status === STATUS_ACTIVE);
  }

  /** 変更者（ログインユーザー）自身の端末が許可端末として登録されているか */
  private get isMyDeviceRegistered(): boolean {
    const myUserId = this.loginUser ? this.loginUser.ewell_user_id : 0;
    if (!myUserId) {
      return false;
    }
    return this.activeDevices.some(d => d.ewell_user_id === myUserId);
  }

  private created() {
    if (!this.isChiefOrAbove) {
      this.$openAlert("この画面を表示する権限がありません。");
      this.$router.back();
      return;
    }
    this.loadDevices();
  }

  /** 一覧取得 */
  private loadDevices() {
    this.postJsonCheck(
      window.base_url + "/api/device-restriction/devices/list",
      { statuses: [] },
      res => {
        this.devices = res.data.devices || [];
        this.staffs = res.data.staffs || [];
        this.deviceRestrictionMode = res.data.device_restriction_mode ?? 0;
        this.maxAllowedDevices = res.data.max_allowed_devices ?? 1;
      }
    );
  }

  /** 設定保存 */
  private async saveSetting() {
    if (this.deviceRestrictionMode === 2 && !this.isMyDeviceRegistered) {
      if (
        !(await this.$openConfirm(
          "【ご注意】\n現在、あなた（変更者）の許可端末が登録されていません。\n\nこのまま厳格運用モードに変更すると、管理者（あなた）も含めて全員ログインできなくなってしまいます。\n\n厳格運用モードに変更するには、現在操作しているこの端末を許可端末として登録することが【必須】となります。\n\nこの端末を許可端末として登録し、厳格運用モードへ変更しますか？",
          "この端末を登録して変更する",
          "キャンセル"
        ))
      ) {
        return;
      }

      // まず最優先で既存の生体認証Passkey情報の引き当て・許可端末バインドを実行
      this.postJsonCheck(
        window.base_url + "/api/device-restriction/devices/register-self",
        {},
        () => {
          // 既存Passkey情報の引き当て成功！
          this.doSaveSetting(
            "本端末を許可端末として登録し、端末制限の設定を保存しました。"
          );
        },
        () => {
          // 生体認証Passkeyが未登録の場合は、フォールバックとして新規Passkey登録プロンプトを起動
          this.registerWebauthnBegin(
            window.auth_backend_url,
            this.postJsonCheck,
            this.$openAlert,
            () => {
              this.doSaveSetting(
                "本端末を許可端末として登録し、端末制限の設定を保存しました。"
              );
            },
            () => {
              this.$openAlert(
                "端末の登録が完了しなかったため、厳格運用モードへの設定変更を中止しました。"
              );
            },
            true // isDeviceRestriction
          );
        }
      );
      return;
    }

    if (!(await this.$openConfirm("端末制限の設定を変更しますか？"))) {
      return;
    }

    this.doSaveSetting("端末制限の設定を保存しました。");
  }

  /** 端末制限設定の保存実行 */
  private doSaveSetting(successMessage: string) {
    this.postJsonCheck(
      window.base_url + "/api/device-restriction/setting/save",
      {
        device_restriction_mode: this.deviceRestrictionMode,
        max_allowed_devices: this.maxAllowedDevices
      },
      () => {
        this.$openAlert(successMessage);
        this.loadDevices();
      }
    );
  }

  /** 承認 */
  private async approve(device: DeviceView) {
    if (
      !(await this.$openConfirm(
        `「${device.staff_name}」のこの端末を承認しますか？`
      ))
    ) {
      return;
    }
    this.postJsonCheck(
      window.base_url + "/api/device-restriction/devices/approve",
      { id: device.id },
      () => {
        this.loadDevices();
      }
    );
  }

  /** 失効・却下 */
  private async revoke(device: DeviceView) {
    if (
      this.deviceRestrictionMode === 2 &&
      device.status === STATUS_ACTIVE &&
      this.activeDevices.length <= 1
    ) {
      this.$openAlert(
        "【失効できません】\n厳格運用モード中は、すべての許可端末を失効させることはできません。\n失効させる前に別の端末を承認・登録するか、セルフ登録モードへ変更してください。"
      );
      return;
    }

    if (
      !(await this.$openConfirm(
        `「${device.staff_name}」のこの端末を無効にしますか？この操作は取り消せません。`
      ))
    ) {
      return;
    }
    this.postJsonCheck(
      window.base_url + "/api/device-restriction/devices/revoke",
      { id: device.id },
      () => {
        this.loadDevices();
      }
    );
  }

  /** 端末追加キー発行 */
  private issueActivationCode() {
    if (!this.issueEwellUserId) {
      return;
    }
    this.postJsonCheck(
      window.base_url + "/api/device-restriction/activation-code/issue",
      { ewell_user_id: this.issueEwellUserId },
      res => {
        this.issuedCode = res.data.activation_code;
        this.issuedExpireMinutes = res.data.expire_minutes;
        this.codeDialog = true;
      }
    );
  }
}
